import useAuthenticatedUserService, { UserName } from 'infrastructure/security/authentication/authenticatedUserService';
import { AvailabilityUserStatus } from '../../availabilityUser/types';
import { InterruptionCreateFormValues, InterruptionCreateRequest, InterruptionType } from '../types';
import { useInterruptionsContext } from '../InterruptionsContext';
import useInterruptionService from '../useInterruptionService';
import { useToast } from 'framework/ui/Toast';
import { CALLBACK_TTL_MINUTES } from '../constants';

type BuildCreateInterruptionRequestParams = {
  currentUserAlias: Nullable<string>;
  currentUserEmail: Nullable<string>;
  currentUserName: string;
  recipient: AvailabilityUserStatus;
  type: InterruptionType;
  values: InterruptionCreateFormValues;
};

const buildCreateInterruptionRequest = ({
  currentUserAlias,
  currentUserEmail,
  currentUserName,
  recipient,
  type,
  values,
}: BuildCreateInterruptionRequestParams): InterruptionCreateRequest => {
  const isEmergencyCall = type === InterruptionType.OnCallEmergency;

  return {
    employeeId: recipient.employeeId,
    creatorId: currentUserAlias || '',
    type,
    body: values.message?.trim() || '',
    context: {
      creatorEmail: currentUserEmail || '',
      creatorName: currentUserName || currentUserEmail || '',
      recipientEmail: recipient.employeeEmail,
      recipientName: recipient.employeeName,
      source: values.source || undefined,
    },
    settings: isEmergencyCall
      ? {
          TTL: `${CALLBACK_TTL_MINUTES}m`,
        }
      : undefined,
  };
};

const getUserFullName = (name: Nullable<UserName>) => (name ? `${name.firstName} ${name.lastName}`.trim() : '');

const useCreateInterruptionSubmit = () => {
  const { success } = useToast();
  const { createInterruption } = useInterruptionService();
  const { addEmergencyInterruption } = useInterruptionsContext();
  const { getUserAlias, getUserName, getWorkingEmail } = useAuthenticatedUserService();

  const createInterruptionFromValues = async (values: InterruptionCreateFormValues, type: InterruptionType) => {
    const recipient = values.recipient;

    if (!recipient) {
      return Promise.reject('Recipient is required');
    }

    const [currentUserAlias, currentUserEmail, currentUserName] = await Promise.all([
      getUserAlias(),
      getWorkingEmail(),
      getUserName().then(getUserFullName),
    ]);

    if (recipient.employeeId === currentUserAlias || recipient.employeeEmail === currentUserEmail) {
      return Promise.reject('You cannot send an interruption to yourself');
    }

    const createdInterruption = await createInterruption(
      buildCreateInterruptionRequest({
        currentUserAlias,
        currentUserEmail,
        currentUserName,
        recipient,
        type,
        values,
      }),
    );

    if (type === InterruptionType.OnCallEmergency) {
      addEmergencyInterruption(createdInterruption);
    } else {
      success('Interruption created successfully');
    }

    return recipient;
  };

  return {
    createInterruptionFromValues,
  };
};

export default useCreateInterruptionSubmit;
